// Libs
import { useEffect, useState } from 'react';
import { add, sub } from 'date-fns';
import type { DateRange } from 'ui-components';
// App
import { FiltersProps, TimeFilters } from './filters.interfaces';
import { FiltersHookResponse } from './filters-hook-response.interface';
import { TIME_PERIOD } from './filters.constants';
import { buildCustomPeriodFromSelectedPeriod, buildDurationStep, buildInitialFilters } from './filters.builders';

const useFilters = (props: FiltersProps): FiltersHookResponse => {
  const { filters: customFilters, onFiltersChange, onGoToPrevious, onGoToNext } = props;
  const [filters, setFilters] = useState<TimeFilters>(customFilters || buildInitialFilters());

  useEffect(() => {
    setFilters(customFilters);
  }, [customFilters]);

  const applyFilters = (updatedFilters: TimeFilters) => {
    setFilters(updatedFilters);

    onFiltersChange && onFiltersChange(updatedFilters);
  };

  const handleSelectTimePeriodFilter = (period: TIME_PERIOD) => {
    const isAlreadySelected = period == filters.selectedTimePeriod;

    if (isAlreadySelected) return;

    const isCustomPeriodSelected = period == TIME_PERIOD.CUSTOM;
    let customPeriod = filters.customPeriod;

    if (isCustomPeriodSelected) {
      customPeriod = buildCustomPeriodFromSelectedPeriod(filters);
    }

    applyFilters({ ...filters, selectedTimePeriod: period, customPeriod });
  };

  const handleSelectSubFilter = (filter: Date) => {
    applyFilters({ ...filters, selectedFilterDate: filter });
  };

  const navigateTo = (navigationDate: Date) => {
    applyFilters({ ...filters, navigationDate, selectedFilterDate: navigationDate });
  };

  const handleGotoPrevious = () => {
    navigateTo(sub(filters.navigationDate, buildDurationStep(filters.selectedTimePeriod)));

    onGoToPrevious && onGoToPrevious();
  };

  const handleGotoNext = () => {
    navigateTo(add(filters.navigationDate, buildDurationStep(filters.selectedTimePeriod)));

    onGoToNext && onGoToNext();
  };

  const handleCustomPeriodChange = (customPeriod: DateRange) => {
    const updatedFilters: TimeFilters = { ...filters, customPeriod };
    const isCustomPeriodComplete = Boolean(customPeriod.start && customPeriod.end);

    setFilters(updatedFilters);

    if (isCustomPeriodComplete) {
      onFiltersChange && onFiltersChange(updatedFilters);
    }
  };

  return {
    filters,
    handleSelectTimePeriodFilter,
    handleSelectSubFilter,
    handleGotoPrevious,
    handleGotoNext,
    handleCustomPeriodChange,
  };
};

export default useFilters;
